import { Upload } from "@mui/icons-material";
import {
	Button,
	type ButtonTypeMap,
	FormHelperText,
	styled,
} from "@mui/material";
import React, { useEffect } from "react";

export const FileInput = styled("input")({
	clip: "rect(0 0 0 0)",
	clipPath: "inset(50%)",
	height: 1,
	overflow: "hidden",
	position: "absolute",
	bottom: 0,
	left: 0,
	whiteSpace: "nowrap",
	width: 1,
});

export type FileButtonOnSelectArgs =
	| { data: string; fileName: string; file: File }
	| "FileTooLarge";

type Props = {
	text: string;
	accept?: string;
	maxSize?: number;
	errorText?: string;
	onSelect: (data: FileButtonOnSelectArgs) => void | Promise<void>;
	iconPath?: string;
	component?: (fileInput: React.ReactNode) => React.ReactNode;
} & Omit<ButtonTypeMap["props"], "children" | "onSelect" | "startIcon">;

const FileButton = ({
	text,
	accept,
	onSelect,
	maxSize,
	errorText,
	iconPath,
	component,
	...buttonProps
}: Props) => {
	const inputRef = React.useRef<HTMLInputElement>(null);
	const timeoutRef = React.useRef<Timer | undefined>(undefined);

	useEffect(
		() => () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		},
		[],
	);

	const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event?.target?.files?.[0];
		if (file && (!maxSize || file.size < maxSize)) {
			const promise = new Promise<string>((resolve) => {
				const reader = new FileReader();
				reader.readAsDataURL(file);
				reader.addEventListener("loadend", () => {
					if (typeof reader.result === "string") resolve(reader.result);
					else resolve("");
				});
			});

			const file64 = await promise;
			onSelect({ data: file64, fileName: file.name, file: file });
			timeoutRef.current = setTimeout(() => {
				if (inputRef.current) inputRef.current.value = "";
				timeoutRef.current = undefined;
			}, 500);
		} else {
			onSelect("FileTooLarge");
		}
	};

	const fileInput = (
		<FileInput ref={inputRef} type="file" onChange={onChange} accept={accept} />
	);
	return component ? (
		component(fileInput)
	) : (
		<>
			<Button startIcon={<Upload />} {...buttonProps} component="label">
				{text}
				{fileInput}
			</Button>
			<FormHelperText error={!!errorText}>{errorText}</FormHelperText>
		</>
	);
};

export default FileButton;
